import { DEFAULTS } from './constants';
import { TagsLayout } from './layouts/TagsLayout';
import { ColumnsLayout } from './layouts/ColumnsLayout';
import { Search } from './features/Search';
import { MaxSelected } from './features/MaxSelected';
import { Creatable } from './features/Creatable';

export interface MultiSelectOptions {
    layout?: 'tags' | 'columns';
    searchable?: boolean;
    showTags?: boolean;
    showCheckboxes?: boolean;
    placeholder?: string;
    countText?: string;
    clearText?: string;
    maxSelected?: number | null;
    maxSelectedMessage?: string;
    availableLabel?: string;
    selectedLabel?: string;
    sortable?: boolean;
    creatable?: boolean;
    creatableText?: string;
    onChange?: (values: string[], instance: MultiSelect) => void;
    onTagClick?: (tag: HTMLElement, value: string | undefined, instance: MultiSelect) => void;
    onTagsRendered?: (container: HTMLElement, instance: MultiSelect) => void;
    onOrderChange?: (orderedValues: string[], instance: MultiSelect) => void;
}

export interface SelectedOption {
    value: string;
    text: string;
}

interface LayoutHandler {
    init(): void;
    renderMenu(): void;
    bindEvents(): void;
    updateSelection(): void;
    getOrderedSelectedValues?(): string[];
    getOrderedTagValues?(): string[];
    destroy?(): void;
}

interface SelectElementWithMultiSelect extends HTMLSelectElement {
    _multiSelect?: MultiSelect;
}

class MultiSelect {
    // Core
    public readonly select!: SelectElementWithMultiSelect;
    public readonly options!: MultiSelectOptions;
    public readonly instanceId!: string;
    public readonly wrapper!: HTMLDivElement;

    // Features
    public readonly search!: Search;
    public readonly maxSelectedFeature!: MaxSelected;
    public readonly creatableFeature!: Creatable;
    public layoutHandler!: LayoutHandler;

    // Configuration
    public isMultiple: boolean = false;
    public isSearchable: boolean = false;
    public showTags: boolean = true;
    public showCheckboxes: boolean = true;
    public placeholder: string = '';
    public countText: string = '';
    public clearText: string = '';
    public layout: string = 'tags';
    public maxSelected: number | null = null;
    public maxSelectedMessage: string = '';
    public availableLabel: string = '';
    public selectedLabel: string = '';
    public isSortable: boolean = false;
    public isCreatable: boolean = false;
    public creatableText: string = '';

    // Layout Elements (Tags)
    public toggle!: HTMLDivElement;
    public displayText!: HTMLSpanElement;
    public menu!: HTMLDivElement;
    public menuBody!: HTMLDivElement;
    public tagsContainer!: HTMLElement;
    public countSpan: HTMLElement | null = null;
    public clearAllBtn: HTMLButtonElement | null = null;

    // Layout Elements (Columns)
    public columnsContainer!: HTMLDivElement;
    public availableColumn!: HTMLDivElement;
    public selectedColumn!: HTMLDivElement;
    public availableList!: HTMLDivElement;
    public selectedList!: HTMLDivElement;

    // Form/Sort State
    private selectName: string = '';
    private hiddenInputsContainer: HTMLDivElement | null = null;

    constructor(selectElement: string | HTMLSelectElement | NodeListOf<Element> | HTMLElement, options: MultiSelectOptions = {}) {
        let el: HTMLSelectElement | null = null;
        if (typeof selectElement === 'string') {
            el = document.querySelector<HTMLSelectElement>(selectElement);
        } else if (selectElement instanceof HTMLSelectElement) {
            el = selectElement;
        } else if (selectElement instanceof HTMLElement && selectElement.tagName === 'SELECT') {
            el = selectElement as HTMLSelectElement;
        } else if (selectElement instanceof NodeList && selectElement[0] instanceof HTMLSelectElement) {
            el = selectElement[0] as HTMLSelectElement;
        }

        if (!el || !el.parentNode) {
            throw new Error('MultiSelect: Target element must be a SELECT tag currently in the DOM');
        }

        if ((el as SelectElementWithMultiSelect)._multiSelect) {
            console.warn('MultiSelect: Element already initialized');
            
            return (el as SelectElementWithMultiSelect)._multiSelect!;
        }

        this.select = el as SelectElementWithMultiSelect;
        this.options = options;
        this.instanceId = this.select.id || `ms-${Math.random().toString(36).substring(2, 11)}`;

        this.wrapper = document.createElement('div');
        this.wrapper.className = 'custom-multiselect-container';
        this.wrapper.id = `ms-container-${this.instanceId}`;

        this.select.parentNode!.insertBefore(this.wrapper, this.select);
        this.wrapper.appendChild(this.select);
        this.select.style.display = 'none';
        this.select._multiSelect = this;

        this.parseConfig(options);
        this.search = new Search(this);
        this.maxSelectedFeature = new MaxSelected(this);
        this.creatableFeature = new Creatable(this);

        if (this.layout === 'columns' && this.isMultiple) {
            this.layoutHandler = new ColumnsLayout(this);
        } else {
            this.layoutHandler = new TagsLayout(this);
        }

        if (this.isSortable && this.isMultiple) {
            this.initSortableInputs();
        }

        this.layoutHandler.init();
        this.layoutHandler.renderMenu();
        this.layoutHandler.bindEvents();
        this.updateSelection(false);

        if (typeof options.onChange === 'function') {
            this.select.addEventListener('change', () => {
                options.onChange!(this.getSelected(), this);
            });
        }
    }

    private initSortableInputs(): void {
        this.selectName = this.select.name;
        
        if (this.selectName) {
            this.select.removeAttribute('name');
            this.hiddenInputsContainer = document.createElement('div');
            this.hiddenInputsContainer.className = 'multiselect-hidden-inputs';
            this.hiddenInputsContainer.style.display = 'none';
            this.wrapper.appendChild(this.hiddenInputsContainer);
        }
    }

    public updateHiddenInputs(orderedValues: string[]): void {
        if (!this.hiddenInputsContainer || !this.selectName) {
            return;
        }

        const fragment = document.createDocumentFragment();
        orderedValues.forEach(value => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = this.selectName;
            input.value = value;
            fragment.appendChild(input);
        });

        this.hiddenInputsContainer.innerHTML = '';
        this.hiddenInputsContainer.appendChild(fragment);
    }

    private parseConfig(options: MultiSelectOptions): void {
        const dataset = this.select.dataset;
        const parseBool = (val: string | undefined, def: boolean): boolean => {
            if (val === undefined || val === null) {
                return def;
            }
            
            return val === 'true' || val === '1';
        };

        this.isMultiple = this.select.multiple;
        this.isSearchable = options.searchable ?? parseBool(dataset.searchable, DEFAULTS.searchable);
        this.showTags = options.showTags ?? parseBool(dataset.showTags, DEFAULTS.showTags);
        this.showCheckboxes = options.showCheckboxes ?? parseBool(dataset.showCheckboxes, DEFAULTS.showCheckboxes);

        this.placeholder = options.placeholder ?? dataset.placeholder ?? (this.isMultiple ? '0 selected' : 'Select...');
        this.countText = options.countText ?? dataset.countText ?? DEFAULTS.countText;
        this.clearText = options.clearText ?? dataset.clearText ?? DEFAULTS.clearText;
        this.layout = options.layout ?? dataset.layout ?? DEFAULTS.layout;

        this.maxSelected = options.maxSelected ?? (dataset.maxSelected ? parseInt(dataset.maxSelected, 10) : DEFAULTS.maxSelected);
        this.maxSelectedMessage = options.maxSelectedMessage ?? dataset.maxSelectedMessage ?? DEFAULTS.maxSelectedMessage;

        this.availableLabel = options.availableLabel ?? dataset.availableLabel ?? DEFAULTS.availableLabel;
        this.selectedLabel = options.selectedLabel ?? dataset.selectedLabel ?? DEFAULTS.selectedLabel;
        this.isSortable = options.sortable ?? parseBool(dataset.sortable, DEFAULTS.sortable);
        this.isCreatable = options.creatable ?? parseBool(dataset.creatable, DEFAULTS.creatable);
        this.creatableText = options.creatableText ?? dataset.creatableText ?? DEFAULTS.creatableText;
    }

    public getOrderedSelected(): string[] {
        if (this.layoutHandler.getOrderedSelectedValues) {
            return this.layoutHandler.getOrderedSelectedValues();
        }
        if (this.layoutHandler.getOrderedTagValues) {
            return this.layoutHandler.getOrderedTagValues();
        }
        return this.getSelected();
    }

    public static init(selector: string, options: MultiSelectOptions = {}): void {
        document.querySelectorAll<HTMLSelectElement>(selector).forEach((element) => {
            new MultiSelect(element, options);
        });
    }

    public static getValues(selector: string | HTMLSelectElement): string[] {
        const el = typeof selector === 'string'
            ? document.querySelector<SelectElementWithMultiSelect>(selector)
            : selector as SelectElementWithMultiSelect;

        if (!el) {
            return [];
        }

        return el._multiSelect?.getSelected() || Array.from(el.selectedOptions || []).map(o => o.value);
    }

    public updateSelection(dispatchChangeEvent: boolean = true): void {
        this.layoutHandler.updateSelection();
        this.maxSelectedFeature.hideError();

        if (dispatchChangeEvent) {
            this.select.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    public clear(dispatchChangeEvent: boolean = true): void {
        Array.from(this.select.options).forEach(opt => opt.selected = false);
        this.updateSelection(dispatchChangeEvent);
    }

    public getSelected(): string[] {
        return Array.from(this.select.selectedOptions).map(opt => opt.value);
    }

    public getSelectedOptions(): SelectedOption[] {
        return Array.from(this.select.selectedOptions).map(opt => ({
            value: opt.value,
            text: opt.text
        }));
    }

    public refresh(): void {
        Array.from(this.wrapper.children).forEach(child => {
            if (child !== this.select && child !== this.hiddenInputsContainer) {
                child.remove();
            }
        });

        if (this.layoutHandler.destroy) {
            this.layoutHandler.destroy();
        }

        this.layoutHandler.init();
        this.layoutHandler.renderMenu();
        this.layoutHandler.bindEvents();
        this.updateSelection(false);
    }

    public destroy(): void {
        if (this.layoutHandler.destroy) {
            this.layoutHandler.destroy();
        }
        this.maxSelectedFeature.destroy();

        if (this.selectName) {
            this.select.name = this.selectName;
        }

        this.wrapper.parentNode?.insertBefore(this.select, this.wrapper);
        this.select.style.display = '';
        delete this.select._multiSelect;
        this.wrapper.remove();
    }

    public disable(): void {
        this.wrapper.classList.add('disabled');
        this.select.disabled = true;
    }

    public enable(): void {
        this.wrapper.classList.remove('disabled');
        this.select.disabled = false;
    }
}

export default MultiSelect;

(window as unknown as { MultiSelect: typeof MultiSelect }).MultiSelect = MultiSelect;